import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import * as _ from 'lodash';
import moment from 'moment';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { AddressModel } from '../../models/complex/address.model';
import {
    TIMERECORDING_TYPE_OF_WORK_CODES,
    TimeRecordingEntryModel,
} from '../../models/complex/time-recording-entry.model';
import { TimeRecordingLimitedEntryModel } from '../../models/complex/time-recording-limited-entry.model';
import { TimesheetAndExpensesP58Model } from '../../models/complex/timesheet-and-expenses-p58.model';
import { TimesheetEntryInterface } from '../../models/complex/timesheet-entry.interface';
import { TimesheetEntryModel } from '../../models/complex/timesheet-entry.model';
import { TimesheetEowModel } from '../../models/complex/timesheet-eow.model';
import { TimesheetGapModel } from '../../models/complex/timesheet-gap.model';
import { TimesheetManualEntryModel } from '../../models/complex/timesheet-manual-entry.model';
import { TimesheetSowModel } from '../../models/complex/timesheet-sow.model';
import { ExpenseModel } from '../../models/low-level/expense.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import {
    TimesheetNotificationModel,
    TimesheetNotificationModelFromJson,
} from '../../models/low-level/timesheet-notification.model';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { TextModel } from '../../models/primitive/text.model';
import { TimesheetAndExpensesUtilities } from '../../utilities/timesheet-and-expenses-utilities';
import { ConfigService } from '../config/config.service';
import { SynchronizationService } from '../synchronization/synchronization.service';
import { WeeklyWorkTimeService } from '../weekly-work-time/weekly-work-time.service';
import { TimesheetIndexedDbStorageService } from './storage-timesheet.indexeddb.service';
import { OdataTimesheetService } from './odata-timesheet.service';
import { WorkScheduleService } from './work-schedule.service';
import { UserMasterService } from '../user-master/user-master.service';
import { StorageTimesheetService } from './storage-timesheet.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { YesNoModalDialogModel } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.model';
import { TranslateService } from '@ngx-translate/core';
import { YesNoModalDialogComponent } from '../../components/yes-no-modal-dialog/yes-no-modal-dialog.component';
import { EventService } from '../event/event.service';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';

@Injectable({
    providedIn: 'root',
})
export class TimesheetAndExpensesService implements InitializableInterface {
    private addresses: AddressModel[] = [];
    private allTimesheet: TimesheetModel[] = [];
    private currentTimesheet: TimesheetModel;
    private submittedTimesheet: TimesheetModel;
    private isSubmitInProgress = false;

    public isInitialized = false;
    public constructor(
        private readonly configService: ConfigService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly localStorageTimesheetService: TimesheetIndexedDbStorageService,
        private readonly storageTimesheetService: StorageTimesheetService,
        private readonly odataTimesheetService: OdataTimesheetService,
        private readonly synchronizationService: SynchronizationService,
        private readonly workScheduleService: WorkScheduleService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly weeklyWorkTimeService: WeeklyWorkTimeService,
        private readonly userMasterService: UserMasterService,
        private readonly modalService: BsModalService,
        private readonly translate: TranslateService,
        private readonly eventService: EventService,
    ) {}

    public init(): Observable<void> {
        if (this.clientManager.isNgmAP0()) {
            return this.localStorageTimesheetService.getAllTimesheets().pipe(
                tap((allTimesheetArray) => (this.allTimesheet = allTimesheetArray)),
                mergeMap(() => this.deletePreviousTimesheets()),
                mergeMap(() => this.storageTimesheetService.getSubmittedTimesheet()),
                tap((submittedTimesheet) => (this.submittedTimesheet = submittedTimesheet)),
                map(() => this.getTodaysTimesheet()),
                mergeMap((todayTimesheet) => this.saveCurrentTimesheet(todayTimesheet)),
                mergeMap((todayTimesheet) => this.saveToAllTimesheetList(todayTimesheet)),
                tap(() => {
                    this.addresses = [];
                    this.isInitialized = true;
                }),
            );
        }

        return this.refreshCurrentTimesheet().pipe(
            mergeMap(() => this.refreshSubmittedTimesheets()),
            mergeMap(() => this.refreshAddresses()),
            map(() => undefined),
        );
    }

    public refreshCurrentTimesheet(): Observable<TimesheetModel> {
        return this.storageTimesheetService
            .getCurrentTimesheet()
            .pipe(tap((currentTimesheet) => this.setCurrentTimesheetAndTriggerUpdate(currentTimesheet)));
    }

    public refreshSubmittedTimesheets(): Observable<TimesheetModel> {
        return this.storageTimesheetService.getSubmittedTimesheet().pipe(
            tap((submittedTimesheet) => {
                this.submittedTimesheet = submittedTimesheet;
                this.weeklyWorkTimeService.getWeeklyWorkTimeValueSubjectUpdate();
            }),
        );
    }

    public refreshTimesheets(): Observable<void> {
        return this.refreshCurrentTimesheet().pipe(
            mergeMap(() => this.refreshSubmittedTimesheets()),
            map(() => undefined),
        );
    }

    public refreshAddresses(): Observable<AddressModel[]> {
        return this.getAddressesFromLocalStorage().pipe(
            tap((addressSet) => {
                this.addresses = addressSet;
                this.isInitialized = true;
            }),
        );
    }

    /**
     * This method is to delete previous timesheet entries from the local storage
     * based on the timesheet start of work is before the 'X' (defined in config service) days
     *
     * @returns an observable of the save state
     */
    public deletePreviousTimesheets(): Observable<void> {
        const previousAllowedDay = this.configService.getPreviousDaysTimesheetLimit();

        const recentTimesheet = this.getAllTimesheets().filter((timesheet: TimesheetModel) =>
            timesheet.startOfWork.isSameOrAfter(moment().subtract(previousAllowedDay, 'days'), 'days'),
        );
        return this.saveAllTimesheets(recentTimesheet);
    }

    /**
     * This method converts the frontend model timesheetAndExpenses to the model used for business logic and GW communication: currentTimesheet.
     *
     * @private
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses - model used specifically on the timesheetAndExpenses component for forms and validation.
     * @returns TimesheetModel | void: currentTimesheet
     * @throws Error on initialization error, or inconsistencies between frontend model and business logic model.
     */
    private prepareTimesheetFormModelForSave(
        timesheetAndExpenses: TimesheetAndExpensesP58Model,
    ): Observable<TimesheetModel | void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: autoSave: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        // no model -> no saving
        if (!timesheetAndExpenses) {
            return of(undefined);
        }

        // SHCN GMP58-977-Timesheet-Problem
        // Fix: Prevent overwriting of the Start Of Work when timesheet is in readonly mode
        // if the timesheet is in readonly mode than return as there will be no data to save
        if (timesheetAndExpenses.readonly) {
            return of(undefined);
        }

        // when there are no entries in the timesheet, we also don't need to save anything
        if (!timesheetAndExpenses.timesheetEntries.value[0]) {
            return of(undefined);
        }

        // start-of-work can be edited in P58, so we need to save the added value
        if (this.clientManager.isNgmP58()) {
            const startOfWorkEntry = <TimesheetSowModel>(
                timesheetAndExpenses.timesheetEntries.value.find((entry) =>
                    TimesheetAndExpensesService.isSowEntry(entry),
                )
            );
            if (startOfWorkEntry && startOfWorkEntry.startDate.value !== this.currentTimesheet.startOfWork) {
                this.currentTimesheet.startOfWork = startOfWorkEntry.startDate.value;
            }
            if (this.configService.getDisplayTimesheetAddressOptions()) {
                // save start and end addresses from timesheet
                this.currentTimesheet.startAddressValue =
                    startOfWorkEntry.startAddressOptions.selectedEntry.model.value;
                const endOfWorkEntry = <TimesheetEowModel>(
                    timesheetAndExpenses.timesheetEntries.value.find((entry) =>
                        TimesheetAndExpensesService.isEowEntry(entry),
                    )
                );
                if (endOfWorkEntry) {
                    this.currentTimesheet.endAddressValue = endOfWorkEntry.endAddressOptions.selectedEntry.model.value;
                }
            }
        }

        // Find travel back entries and check if the date/time was modified. If yes, overwrite the entries from the
        // time recording with the modified values
        const timesheet = this.clientManager.isNgmAP0()
            ? this.getTimesheetForDate(timesheetAndExpenses.timesheetEntries.value[0].startDate.value)
            : this.getCurrentTimesheet();

        const travelBackEntries = <TimesheetEntryModel[]>(
            timesheetAndExpenses.timesheetEntries.value.filter((entry) =>
                TimesheetAndExpensesService.isTravelBackEntry(entry),
            )
        );
        travelBackEntries.forEach((entry) => {
            // Get matching timesheet notification model (with its corresponding entries)
            const timesheetNotification = timesheet.notifications.find((timesheetNotificationModel) =>
                timesheetNotificationModel.notificationId.equalsTo(entry.notificationId),
            );

            if (!timesheetNotification) {
                throw new Error(
                    `TimesheetAndExpensesService: autoSave: Could not find timesheet notification for notification ${entry.notificationId.toString()} (from timesheet entry ${
                        entry.workCode
                    })`,
                );
            }

            // Find matching travel back entry in saved timesheet
            const timesheetTravelBack = timesheetNotification.timesheetEntries.find((timesheetEntry) =>
                TimesheetAndExpensesService.isTravelBackEntry(timesheetEntry),
            );

            if (!timesheetTravelBack) {
                throw new Error(
                    `TimesheetAndExpensesService: prepareTimesheetForSave: Could not find travel back entry in entries of timesheet notification for notification ${timesheetNotification.notificationId.toString()}`,
                );
            }

            // Sanity check: Make sure both entries start at the same time
            if (!entry.startTime.value.isSame(timesheetTravelBack.startTime.value)) {
                this.log.error(
                    'TimesheetAndExpensesService: prepareTimesheetForSave: Unable to find matching travel back slot in timesheet',
                );
                return;
            }

            // Check if the travel back time was modified
            if (!entry.endTime.value.isSame(timesheetTravelBack.endTime.value)) {
                // If yes, save modified time in timesheet notification model
                timesheetTravelBack.endTime.value.set(entry.endTime.value.toObject());
            }
        });
        // save manual entries
        timesheet.manualTimesheetEntries = <TimesheetManualEntryModel[]>(
            timesheetAndExpenses.timesheetEntries.value.filter((entry) =>
                TimesheetAndExpensesService.isManualEntry(entry),
            )
        );

        const saveNotificationExpenses$: Array<Observable<TimesheetModel | undefined>> = [];

        // save expenses added/deleted to notifications and additional, also the distance added only for additional expenses
        _.forOwn(timesheetAndExpenses.expensesDictionary, (expensesArrayModel, notificationIdString) => {
            if (expensesArrayModel && expensesArrayModel.value) {
                const id = NotificationIdentifierModel.fromString(notificationIdString);
                saveNotificationExpenses$.push(
                    this.saveNotificationExpenses(
                        id,
                        expensesArrayModel.value,
                        id.qmNum ? new TextModel('Distance') : timesheetAndExpenses.additionalDistance,
                    ),
                );
            }
        });

        // http s://stackoverflow.com/questions/40303012/observable-forkjoin-doesnt-execute.
        // In c ase if the variable saveNotificationExpenses$ is empty, forkJoin([]) was throwing an error
        // 'for kJoin no elements in sequence'and it was failing to redirect to dashboard
        // henc e in such situation, i have handled with if condition in case if this is empty pushing of(undefined),
        // foun d solution in above stackover flow link

        if (saveNotificationExpenses$.length === 0) {
            saveNotificationExpenses$.push(of(undefined));
        }
        return forkJoin(saveNotificationExpenses$).pipe(map(() => timesheet));
    }

    /**
     * Method to automatically save changes in the timesheet form from the timesheetAndExpenses component to local store (indexeddb currently).
     *
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses - model used on timesheet-and-expenses component for forms and validation
     * @returns TimesheetModel
     */
    public autoSaveTimesheetForm(
        timesheetAndExpenses: TimesheetAndExpensesP58Model,
    ): Observable<TimesheetModel | void> {
        return this.prepareTimesheetFormModelForSave(timesheetAndExpenses).pipe(
            mergeMap((timesheet) => {
                if (timesheet) {
                    if (this.clientManager.isNgmAP0()) {
                        return this.saveToAllTimesheetList(timesheet);
                    } else {
                        return this.storageTimesheetService.saveCurrentTimesheetToLocal(timesheet);
                    }
                }
                return of(undefined);
            }),
        );
    }

    public isCurrentTimesheetUpToDate(): Observable<boolean> {
        const lastSyncTimestamp = this.currentTimesheet.saveTimestamp;

        return this.storageTimesheetService.getCurrentTimesheet().pipe(
            map((ts) => {
                if (ts.saveTimestamp > lastSyncTimestamp) {
                    this.setCurrentTimesheetAndTriggerUpdate(ts);
                    return false;
                }
                return true;
            }),
            tap(() => this.weeklyWorkTimeService.getWeeklyWorkTimeValueSubjectUpdate()),
        );
    }

    public saveCurrentTimesheet(timesheet?: TimesheetModel): Observable<TimesheetModel> {
        if (timesheet && !this.isSubmitInProgress) {
            this.setCurrentTimesheetAndTriggerUpdate(timesheet);
        }

        return this.isCurrentTimesheetUpToDate().pipe(
            mergeMap((isTimesheetUpToDate) => {
                if (!isTimesheetUpToDate) {
                    this.modalService.show(YesNoModalDialogComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_MEDIUM,
                        initialState: {
                            model: new YesNoModalDialogModel(
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_TITLE'),
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_BODY'),
                                <string>this.translate.instant('TIMESHEET_REMOTE_UPDATE_REQUIRED_YES_BUTTON'),
                            ),
                        },
                        ariaLabelledBy: 'yes-no-modal-title',
                    });
                    return of(this.currentTimesheet);
                }
                return this.storageTimesheetService.saveCurrentTimesheetToConcurrentStore(this.currentTimesheet);
            }),
            tap(() => this.weeklyWorkTimeService.getWeeklyWorkTimeValueSubjectUpdate()),
        );
    }

    /**
     * This method is saving the current timesheet state from the timesheetAndExpenses component to concurrent store and local store.
     *
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses
     * @returns TimesheetModel
     */
    public saveAndUploadTimesheet(
        timesheetAndExpenses: TimesheetAndExpensesP58Model,
    ): Observable<TimesheetModel | void> {
        return this.prepareTimesheetFormModelForSave(timesheetAndExpenses).pipe(
            mergeMap((timesheet) => {
                if (timesheet) {
                    if (this.clientManager.isNgmAP0()) {
                        return this.saveToAllTimesheetList(timesheet);
                    } else {
                        return this.saveCurrentTimesheet(timesheet);
                    }
                }
                return of(undefined);
            }),
        );
    }

    public submit(model: TimesheetAndExpensesP58Model): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: submit: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        return this.odataTimesheetService.saveTimesheet(model).pipe(
            mergeMap(() => {
                // For  NGMAA and SA, find the timesheet that we just submitted and set it to "submitted"
                if (this.clientManager.isNgmAP0()) {
                    const startOfWork = model.timesheetEntries.value.find((entry) =>
                        TimesheetAndExpensesService.isSowEntry(entry),
                    );
                    if (!startOfWork) {
                        throw new Error(
                            'TimesheetAndExpensesService: submit: Unable to find SoW entry in submitted timesheet!',
                        );
                    }
                    const timesheet = this.getAllTimesheets().find((timesheetModel) =>
                        timesheetModel.startOfWork.isSame(startOfWork.startDate.value, 'day'),
                    );
                    if (!timesheet) {
                        throw new Error(
                            'TimesheetAndExpensesService: submit: Could not find the stored timesheet that we just submitted!',
                        );
                    }
                    timesheet.isSubmitted = true;
                    return this.saveToAllTimesheetList(timesheet);
                } else {
                    // For  P58, set the current timesheet as the new "previous/submitted" one and create a new "current" timesheet
                    return this.saveSubmittedTimesheet(this.getCurrentTimesheet());
                }
            }),
            mergeMap(() => {
                if (this.configService.getDisplayWeeklyWorkTime()) {
                    const totalDuration = this.getTotalDurationOfCurrentTimesheetEntries() ?? moment.duration(0);
                    const startOfWorkDate = this.getStartOfWork();
                    this.log.debug(
                        `TimesheetAndExpensesService: submit: Total duration: ${totalDuration}  for Date: ${startOfWorkDate} is `,
                    );
                    return this.weeklyWorkTimeService.updateWorkTimeListFromTimeSheet(startOfWorkDate, totalDuration);
                }
                return of(undefined);
            }),
            mergeMap(() => {
                // set  model to 'readonly' to prohibit any further changes
                model.readonly = true;
                if (this.clientManager.isNgmP58()) {
                    this.setNewEmptyCurrentTimesheet();
                    return this.storageTimesheetService
                        .saveCurrentTimesheetToConcurrentStore(this.currentTimesheet)
                        .pipe(tap(() => this.weeklyWorkTimeService.getWeeklyWorkTimeValueSubjectUpdate()));
                }
                return of(undefined);
            }),
            mergeMap(() => {
                // star t sync
                this.log.debug('TimesheetAndExpensesService: submit: sendOfflineData');
                return this.synchronizationService.sendOfflineData();
            }),
        );
    }

    public getTodaysTimesheet(): TimesheetModel {
        return this.getTimesheetForDate(moment());
    }

    public getTimesheetForDate(date: moment.Moment): TimesheetModel {
        if (!this.clientManager.isNgmAP0()) {
            throw new Error("TimesheetAndExpensesService: Called 'getTimesheetForDate()' on an unsupported backend!");
        }

        let matchingTimesheet = this.getAllTimesheets().find((timesheet) => timesheet.startOfWork.isSame(date, 'day'));

        if (!matchingTimesheet) {
            const workSchedule = this.workScheduleService.getWorkScheduleByDate(date);
            matchingTimesheet = new TimesheetModel(
                workSchedule.startOfWork,
                [],
                [],
                [],
                workSchedule.endOfWork,
                false,
                workSchedule.attendanceType,
            );
        }
        return matchingTimesheet;
    }

    public getCurrentTimesheet(): TimesheetModel {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getCurrentTimesheet: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        if (!this.currentTimesheet) {
            throw new Error(`TimesheetAndExpensesService: getCurrentTimesheet: currentTimesheet is undefined`);
        }
        const timesheet = this.currentTimesheet;
        if (
            this.userMasterService.isPaceOnlineUser() &&
            this.clientManager.isNgmP58() &&
            !timesheet.startOfWork.isValid()
        ) {
            timesheet.startOfWork = moment().startOf('day');
            timesheet.endOfWork = moment().endOf('day');
        }
        return timesheet;
    }

    public getSubmittedTimesheet(): TimesheetModel {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getSubmittedTimesheet: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        if (!this.submittedTimesheet) {
            throw new Error(`TimesheetAndExpensesService: getSubmittedTimesheet: submittedTimesheet is undefined`);
        }
        return this.submittedTimesheet;
    }

    public saveSubmittedTimesheet(timesheet: TimesheetModel): Observable<TimesheetModel> {
        this.submittedTimesheet = timesheet;
        return this.storageTimesheetService.saveSubmittedTimesheet(timesheet);
    }

    public getStartOfWork(): moment.Moment {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        if (!this.currentTimesheet) {
            throw new Error(`TimesheetAndExpensesService: getStartOfWork: currentTimesheet is undefined`);
        }
        return this.currentTimesheet.startOfWork;
    }

    public saveStartOfWork(newStartOfWork: moment.Moment): Observable<TimesheetModel> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveStartOfWork: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        if (!this.currentTimesheet) {
            throw new Error(`TimesheetAndExpensesService: saveStartOfWork: currentTimesheet is undefined`);
        }
        this.currentTimesheet.startOfWork = newStartOfWork;
        this.eventService.triggerStartWorkUpdated(true);
        return this.saveCurrentTimesheet(this.currentTimesheet);
    }

    public deleteSubmittedNotificationInfoFromTimesheet(): Observable<TimesheetModel> {
        const notifications = this.currentTimesheet.notifications.filter(
            (notification: TimesheetNotificationModelFromJson) => !notification.isConfirmationSubmitted,
        );
        this.currentTimesheet.notifications = notifications || [];
        return this.saveCurrentTimesheet(this.currentTimesheet);
    }

    /**
     * This method is to retrieve timesheet related information (expenses, distance etc) for a notification saved as a TimesheetNotificationModel
     *
     * @param notificationId the notification identifier
     * @param timesheet the timesheet to search in
     * @returns the TimesheetNotificationModel or undefined if not found
     * @throws Error if the service is not initialized
     */
    public getTimesheetNotification(
        notificationId: NotificationIdentifierModel,
        timesheet?: TimesheetModel,
    ): TimesheetNotificationModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getTimesheetNotification: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        const timesheetToSearch = timesheet ? timesheet : this.getCurrentTimesheet();

        return timesheetToSearch.notifications.find(
            (n) => n.isConfirmationSubmitted && n.notificationId.equalsTo(notificationId),
        );
    }

    /**
     * This method is to save all timesheet related information (distance, expenses, time-recording) for a notification coming from confirmation screen to the local store
     *
     * @param notification the notification to save
     * @param expenses the expenses to save
     * @param timeRecordingEntries the time-recording entries to save
     * @param distance the distance to save
     * @param isConfirmationSubmitted if the notification is submitted
     * @param isAfterHoursCallOut
     * @returns the saved TimesheetModel or undefined if not found
     * @throws Error if the service is not initialized
     */
    public saveTimesheetNotification(
        notification: NotificationModel,
        expenses: ExpenseModel[],
        timeRecordingEntries: TimeRecordingEntryModel[],
        distance: TextModel,
        isConfirmationSubmitted = true,
        isAfterHoursCallOut = false,
    ): Observable<TimesheetModel | void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveTimesheetNotification: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        // P58  has address properties in timesheet entries for notifications, NGMAA does not.
        const addressInOutModel = this.clientManager.isNgmP58();
        const enableStandbyTravel = this.configService.getEnableStandbyTravel();

        // transform TimeRecordingEntryModel[] to TimesheetEntryModel[]
        let isDistanceAdded = false;
        const timesheetEntryModelArray = timeRecordingEntries.map((timeRecordingEntry) => {
            let workCode = timeRecordingEntry.getTypeOfWorkCode();
            let activityType = '';
            let meansOfTransport = '';

            // For NGMAA and SA, instead of TRTO and TRBK, we use TRAV (paid travel) or TRAU (unpaid travel)
            // For NGMP58, along with TRTO and TRBK, we use TRTU (unpaid travel to) and TRBU (unpaid travel back)
            if (
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                (this.clientManager.isNgmAP0() || this.configService.getDisplayMeansOfTransport())
            ) {
                if (timeRecordingEntry.meansOfTransport) {
                    meansOfTransport = timeRecordingEntry.meansOfTransport.selectedEntry?.value;
                }
            }

            // In NGMAA, for India we need to report the travel back destination (home or office) as well.
            if (
                this.clientManager.isNgmAAIndia() &&
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                timeRecordingEntry.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            ) {
                activityType = timeRecordingEntry.destination.selectedEntry.value;
            }

            // If standby travel enabled, then default travel-back entry must be standby
            if (
                enableStandbyTravel &&
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                timeRecordingEntry.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK
            ) {
                workCode = TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY;
            }

            // add distance to TRTO timerecording entry.
            if (
                this.clientManager.isNgmP58() &&
                !isDistanceAdded &&
                timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                timeRecordingEntry.typeOfWorkCode === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO
            ) {
                isDistanceAdded = true;
                return new TimesheetEntryModel(
                    notification.identifier,
                    timeRecordingEntry.startDateTime,
                    timeRecordingEntry.endDateTime,
                    workCode,
                    activityType,
                    notification.address.getTimesheetAddress(),
                    addressInOutModel,
                    distance,
                    meansOfTransport,
                    false,
                );
            }

            return new TimesheetEntryModel(
                notification.identifier,
                timeRecordingEntry.startDateTime,
                timeRecordingEntry.endDateTime,
                workCode,
                activityType,
                notification.address.getTimesheetAddress(),
                addressInOutModel,
                undefined,
                meansOfTransport,
                enableStandbyTravel &&
                    timeRecordingEntry instanceof TimeRecordingLimitedEntryModel &&
                    this.configService.getIsTravelBillable(notification.orderType),
                isAfterHoursCallOut,
            );
        });

        // When modifying the timesheet from a different component (e.g. confirmation) make sure that newest version of timesheet is loaded from concurrent store.
        return this.refreshCurrentTimesheet().pipe(
            mergeMap(() => {
                // Distance is only part of the NGMP58, NGMAA does not have Distance CRM field
                if (this.clientManager.isNgmP58()) {
                    if (!isDistanceAdded) {
                        this.log.warn(
                            `TimesheetAndExpensesService: saveTimesheetNotification: No time recording entry exists for TRTO for notification ${notification.identifier.toString()}, so distance cannot be added to notification. `,
                        );
                    }
                    const currentTimesheet = this.getCurrentTimesheet();
                    let timesheetNotification = currentTimesheet.notifications.find((currentNotificationModel) => {
                        return currentNotificationModel.notificationId.equalsTo(notification.identifier);
                    });
                    if (!timesheetNotification) {
                        timesheetNotification = new TimesheetNotificationModel(
                            notification.identifier,
                            [],
                            [],
                            new TextModel('Distance'),
                            notification.address.completeName,
                            isConfirmationSubmitted,
                        );
                        currentTimesheet.notifications.push(timesheetNotification);
                    }
                    timesheetNotification.expenses = expenses;
                    timesheetNotification.distance = distance;
                    timesheetNotification.timesheetEntries = timesheetEntryModelArray;
                    timesheetNotification.isConfirmationSubmitted = isConfirmationSubmitted;
                    // sort notifications of currentTimesheet by startDate before saving
                    TimesheetAndExpensesUtilities.sortNotificationsByFirstTimesheetEntryStartTime(currentTimesheet);
                    this.setCurrentTimesheetAndTriggerUpdate(currentTimesheet);
                    return this.storageTimesheetService.saveCurrentTimesheetToLocal(currentTimesheet);
                } else {
                    // For SA, we do not display TRAV entries, if user selects Phone Fix task
                    const firstTravelOrPhoneFixDate = timesheetEntryModelArray.find((timesheetEntry) => {
                        return (
                            timesheetEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO ||
                            timesheetEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY ||
                            (this.clientManager.isNgmSA() &&
                                (timesheetEntry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX ||
                                    timesheetEntry.workCode.value ===
                                        TIMERECORDING_TYPE_OF_WORK_CODES.PHONE_FIX_EXPENSES))
                        );
                    });
                    if (!firstTravelOrPhoneFixDate) {
                        throw new Error(
                            `TimesheetAndExpensesService: saveTimesheetNotification: Notification  ${notification.identifier.toString()} did not have any TRTO entries: ${timesheetEntryModelArray
                                .map((e) => e.workCode)
                                .join(' ')}`,
                        );
                    }
                    // Based on notification TRTO it will be decided the entry belongs to which timesheet

                    let matchedTimesheet = this.getAllTimesheets().find(
                        (timesheet) =>
                            firstTravelOrPhoneFixDate &&
                            timesheet.startOfWork.isSame(firstTravelOrPhoneFixDate.startDate.value, 'days'),
                    );
                    // if we still don't have a timesheet, it means that TRTO was not today (otherwise it would have matched currentTimesheet)
                    // and it did not match any previous timesheets. This is impossible unless the app was re-installed, since sending TRTO would
                    // have created a timesheet on the previous day. If that timesheet is not there, the app was deleted and reinstalled.
                    if (!matchedTimesheet) {
                        const workSchedule = this.workScheduleService.getWorkScheduleByDate(
                            firstTravelOrPhoneFixDate.startDate.value,
                        );
                        if (!workSchedule) {
                            throw new Error(
                                `TimesheetAndExpensesService: saveTimesheetNotification: Could not create work schedule model for date '${firstTravelOrPhoneFixDate.startDate.value}'`,
                            );
                        }

                        matchedTimesheet = new TimesheetModel(
                            workSchedule.startOfWork,
                            [],
                            [],
                            [],
                            workSchedule.endOfWork,
                            false,
                        );
                    }
                    let timesheetNotification = matchedTimesheet.notifications.find((matchedTimesheetNotification) =>
                        matchedTimesheetNotification.notificationId.equalsTo(notification.identifier),
                    );

                    if (!timesheetNotification) {
                        timesheetNotification = new TimesheetNotificationModel(
                            notification.identifier,
                            [],
                            [],
                            new TextModel('Distance'),
                            notification.address.completeName,
                            isConfirmationSubmitted,
                        );
                        matchedTimesheet.notifications.push(timesheetNotification);
                    }

                    timesheetNotification.expenses = expenses;
                    timesheetNotification.distance = distance;
                    timesheetNotification.timesheetEntries = timesheetEntryModelArray;
                    timesheetNotification.isConfirmationSubmitted = isConfirmationSubmitted;
                    // sort notifications of matchedTimesheet by startDate before saving
                    TimesheetAndExpensesUtilities.sortNotificationsByFirstTimesheetEntryStartTime(matchedTimesheet);

                    this.setCurrentTimesheetAndTriggerUpdate(matchedTimesheet);

                    return this.storageTimesheetService
                        .saveCurrentTimesheetToLocal(matchedTimesheet)
                        .pipe(mergeMap((timesheet) => this.saveToAllTimesheetList(timesheet)));
                }
            }),
        );
    }

    public getAddresses(): AddressModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: getAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        return this.addresses;
    }

    public getAddressesFromLocalStorage(): Observable<AddressModel[]> {
        return this.localStorageTimesheetService
            .getAddresses()
            .pipe(map((addresses) => _.sortBy(addresses, 'addrNumber')));
    }

    public saveAddresses(addresses: AddressModel[]): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: saveAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        this.addresses = addresses;
        return this.localStorageTimesheetService.saveAddresses(addresses);
    }

    public getAllTimesheets(): TimesheetModel[] {
        if (!this.allTimesheet) {
            throw new Error(`TimesheetAndExpensesService: getAllTimesheets: allTimesheet is undefined!`);
        }
        return this.allTimesheet;
    }

    public saveAllTimesheets(timesheetArray: TimesheetModel[]): Observable<void> {
        this.allTimesheet = timesheetArray;
        return this.localStorageTimesheetService.saveAllTimesheets(timesheetArray);
    }

    /**
     * This method is to return sorted timesheet based on startOfWork
     *
     * @returns sorted timesheet
     */
    public getAllTimesheetsSortByStartOfWork(): TimesheetModel[] {
        return this.getAllTimesheets().sort((timesheet1, timesheet2) =>
            timesheet1.startOfWork.isBefore(timesheet2.startOfWork, 'days') ? -1 : 1,
        );
    }

    public saveToAllTimesheetList(timesheet: TimesheetModel): Observable<void> {
        const matchedTimesheetIndex = this.getAllTimesheets().findIndex((previousTimesheet) =>
            previousTimesheet.startOfWork.isSame(timesheet.startOfWork, 'day'),
        );
        // Upda te matching timesheet also to variable . If none was found, push the new timesheet into the list
        if (matchedTimesheetIndex >= 0) {
            this.allTimesheet[matchedTimesheetIndex] = timesheet;
        } else {
            this.allTimesheet.push(timesheet);
        }
        return this.localStorageTimesheetService.saveAllTimesheets(this.allTimesheet);
    }

    public addAddress(newAddress: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: addAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        // check if the address is already existing locally. We ignore the addrNumber since we assume that it is already
        // set if the address is existing and we want to match manually entered addresses with existing, predefined
        // addresses
        const existingAddress = this.addresses.find((address) =>
            newAddress.getTimesheetAddress().equalsTo(address.getTimesheetAddress()),
        );
        // make sure the address is not contained in the stored addresses yet
        if (!existingAddress) {
            this.addresses.unshift(newAddress); // inse rt new address on top of list
            return this.saveAddresses(this.addresses);
        }

        return of(undefined);
    }

    public resetAddresses(): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: resetAddresses: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        this.addresses.map((address) => (address.visible = true));
        return this.saveAddresses(this.addresses);
    }

    public hideAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: hideAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }
        const index = this.addresses.findIndex((addr) => address.addrNumber === addr.addrNumber);
        this.addresses[index].visible = false;
        return this.saveAddresses(this.addresses);
    }

    /**
     * Removes an address from the local storage
     *
     * @param address the address to remove
     * @returns an observable that completes when the address is removed
     * @throws Error if the service is not initialized
     */
    public deleteAddress(address: AddressModel): Observable<void> {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: deleteAddress: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        _.remove(this.addresses, (addr) => address.getTimesheetAddress().equalsTo(addr.getTimesheetAddress()));
        return this.saveAddresses(this.addresses);
    }

    public getTotalDurationOfCurrentTimesheetEntries(): moment.Duration | undefined {
        return this.currentTimesheet.getTimesheetEntries().reduce((previousValue, currentValue) => {
            if (
                TimesheetAndExpensesService.isManualEntry(currentValue) &&
                currentValue.taskTypeDropdown.selectedEntry &&
                !this.configService.getTimesheetPauseCodes(currentValue.taskTypeDropdown.selectedEntry.value)
            ) {
                return previousValue.add(
                    moment.duration(currentValue.endDate.value.diff(currentValue.startDate.value)),
                );
            }
            if (
                TimesheetAndExpensesService.isNormalEntry(currentValue) &&
                currentValue.workCode?.value !== TIMERECORDING_TYPE_OF_WORK_CODES.BREAK
            ) {
                return previousValue.add(
                    moment.duration(currentValue.endDate.value.diff(currentValue.startDate.value)),
                );
            }
            return moment.duration(previousValue);
        }, moment.duration(0));
    }

    /**
     * This method is to determine whether the working hours in timesheet is greater than total working hours (10)
     * EOW - SOW - (Pause + Break)
     *
     * @param {TimesheetAndExpensesP58Model} timesheetAndExpenses
     * @returns true if the working hours are greater than total working hours
     * @throws Error if the service is not initialized
     */
    public isExceedingWorkHours(timesheetAndExpenses: TimesheetAndExpensesP58Model): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `TimesheetAndExpensesService: isExceedingWorkHours: Trying to call TimesheetAndExpensesService before initialization!`,
            );
        }

        const startOfWork = timesheetAndExpenses.timesheetEntries.value.find((entry) =>
            TimesheetAndExpensesService.isSowEntry(entry),
        );
        const endOfWork = timesheetAndExpenses.timesheetEntries.value.find((entry) =>
            TimesheetAndExpensesService.isEowEntry(entry),
        );
        if (startOfWork && startOfWork.startDate && endOfWork && endOfWork.endDate) {
            // Calculate for break and pause times only if total timesheet hours is more than configService.getMaxTotalWorkingHours()
            const totalWorkingTime = moment.duration(endOfWork.endDate.value.diff(startOfWork.startDate.value));
            if (totalWorkingTime.asHours() > this.configService.getMaxWorkingHoursInTimesheet()) {
                let pauseTime = moment.duration(0);
                let breakTime = moment.duration(0);
                timesheetAndExpenses.timesheetEntries.value.map((entry) => {
                    // entry.travelType is available only when we "add time slot" not when we "add travel back"
                    if (
                        TimesheetAndExpensesService.isManualEntry(entry) &&
                        !entry.travelType &&
                        this.configService.getTimesheetPauseCodes(entry.taskTypeDropdown.selectedEntry.value)
                    ) {
                        pauseTime = pauseTime.add(moment.duration(entry.endDate.value.diff(entry.startDate.value)));
                    }
                    if (
                        TimesheetAndExpensesService.isNormalEntry(entry) &&
                        entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.BREAK
                    ) {
                        breakTime = breakTime.add(moment.duration(entry.endDate.value.diff(entry.startDate.value)));
                    }
                });
                // subt ract the pause and breaks to get actual working hours
                const workingHours = totalWorkingTime.subtract(pauseTime).subtract(breakTime).asHours();
                return workingHours > this.configService.getMaxWorkingHoursInTimesheet();
            }
        } else {
            this.log.error(
                `TimesheetAndExpensesService: isExceedingWorkHours: SOW: ${startOfWork} and/or EOW: ${endOfWork} is undefined`,
            );
        }

        return false;
    }

    // type guard function for TimesheetGapModel
    public static isGapEntry(entry: TimesheetEntryInterface): entry is TimesheetGapModel {
        return entry instanceof TimesheetGapModel;
    }

    // type guard function for TimesheetManualEntryModel
    public static isManualEntry(entry: TimesheetEntryInterface): entry is TimesheetManualEntryModel {
        return entry instanceof TimesheetManualEntryModel;
    }

    // type guard function for TimesheetEntryModel
    public static isNormalEntry(entry: TimesheetEntryInterface): entry is TimesheetEntryModel {
        return entry instanceof TimesheetEntryModel;
    }

    // type guard function for TimesheetSoWModel
    public static isSowEntry(entry: TimesheetEntryInterface): entry is TimesheetSowModel {
        return entry instanceof TimesheetSowModel;
    }

    // type guard function for TimesheetEoWModel
    public static isEowEntry(entry: TimesheetEntryInterface): entry is TimesheetEowModel {
        return entry instanceof TimesheetEowModel;
    }

    public static isTravelBackEntry(entry: TimesheetEntryInterface): boolean {
        return (
            entry instanceof TimesheetEntryModel &&
            (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRBK ||
                entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_BACK_STANDBY)
        );
    }

    public static isTravelToEntry(entry: TimesheetEntryInterface): boolean {
        return (
            entry instanceof TimesheetEntryModel &&
            (entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRTO ||
                entry.workCode.value === TIMERECORDING_TYPE_OF_WORK_CODES.TRAVEL_TO_STANDBY)
        );
    }

    /**
     * This method is to save changes done to timesheet-expenses and also the additional distance value
     *
     * @param notificationId the notification identifier
     * @param notificationExpenses the expenses to save
     * @param distance the distance to save
     * @returns the saved TimesheetModel
     */
    private saveNotificationExpenses(
        notificationId: NotificationIdentifierModel,
        notificationExpenses: ExpenseModel[],
        distance: TextModel,
    ): Observable<TimesheetModel> {
        const currentTimesheet = this.currentTimesheet;
        let timesheetNotification = currentTimesheet.notifications.find((currentNotificationModel) =>
            currentNotificationModel.notificationId.equalsTo(notificationId),
        );
        if (!timesheetNotification) {
            timesheetNotification = new TimesheetNotificationModel(
                notificationId,
                [],
                [],
                new TextModel('Distance'),
                '',
                true,
            );
            currentTimesheet.notifications.push(timesheetNotification);
        }
        // save distance only for additional expenses, distance for notifications are saved while saving confirmation
        if (!notificationId.qmNum) {
            timesheetNotification.distance = distance;
        }
        timesheetNotification.expenses = notificationExpenses;
        return of(currentTimesheet);
    }

    private setCurrentTimesheetAndTriggerUpdate(timesheet: TimesheetModel): void {
        this.currentTimesheet = timesheet;
        this.addAddressFromExistingManualTimesheetEntries(timesheet.manualTimesheetEntries);
        this.eventService.triggerUpdateTimesheet();
    }

    private addAddressFromExistingManualTimesheetEntries(manualTimesheetEntries: TimesheetManualEntryModel[]): void {
        if (this.isInitialized) {
            manualTimesheetEntries.forEach((manualTimesheet) => {
                if (manualTimesheet.address) {
                    this.addAddress(AddressModel.fromTimesheetAddress(manualTimesheet.address));
                }
            });
        }
    }

    private setNewEmptyCurrentTimesheet(): void {
        this.setCurrentTimesheetAndTriggerUpdate(
            new TimesheetModel(moment.invalid(), [], [], [], moment.invalid(), false),
        );
        this.eventService.triggerStartWorkUpdated(false);
    }

    public saveSubmitStatus(status: boolean): void {
        this.isSubmitInProgress = status;
    }
}
